"use client";

import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import Link from "next/link";
import { ChevronDown } from "lucide-react";
import Image from "@/helpers/Image";
import { GROQImage, IHeader } from "@/sanity/lib/definitions";
import { cn } from "@/lib/utils";
import { useGlobalPathname } from "./HeaderContext";
import { CTAWrapper } from "@/components/global/utils/CTAWrapper";
import { IPageLink, resolvePageLink } from "@/sanity/globals/CTA";

function Logo({
  isLight = false,
  solidIcon = false,
  className,
}: {
  isLight?: boolean;
  solidIcon?: boolean;
  className?: string;
}) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="161"
      height="25"
      fill="none"
      viewBox="0 0 161 25"
      aria-hidden
      className={cn(
        "w-[20.125rem] h-[3.125rem] shrink-0",
        isLight && "invert",
        className,
      )}>
      <path
        fill="#fff"
        d="M23 1.977V24h-2.11V6.982c0-1.165-1.052-2.11-2.35-2.11-1.297 0-2.35.945-2.35 2.11V24h-2.413V9.001c0-1.165-1.051-2.11-2.35-2.11-1.298 0-2.35.945-2.35 2.11V24H6.666V11.021c0-1.165-1.053-2.11-2.35-2.11s-2.35.945-2.35 2.11V24H0V6.323L22.998 0z"
        opacity="0.81"
        style={{ mixBlendMode: solidIcon ? "normal" : "overlay" }}
      />
      <path
        fill="#CFCFD0"
        d="m41.009 9.15-3.996 10.494h-.837L33.224 8.313 31.892 7.8v-.36h4.797v.36l-1.539.495 2.313 9.072h-.432l3.717-10.071h.855l4.059 10.359-.738.27 2.196-9.63-1.512-.495v-.36h3.771v.36l-1.296.495-2.79 11.349h-.837L40.397 9.402zm11.996 9.792q.603 0 1.071-.306.477-.315.747-.981.279-.675.279-1.737 0-1.161-.27-1.953t-.756-1.197a1.72 1.72 0 0 0-1.152-.414q-.603 0-1.08.315-.477.306-.747.981-.27.666-.27 1.728 0 1.152.261 1.953.27.792.756 1.206.495.405 1.161.405m-.081.756q-1.179 0-2.07-.495a3.6 3.6 0 0 1-1.377-1.395q-.486-.9-.486-2.061 0-1.206.513-2.142a3.84 3.84 0 0 1 1.422-1.467q.909-.54 2.07-.54 1.188 0 2.07.504a3.44 3.44 0 0 1 1.377 1.386q.495.891.495 2.061 0 1.206-.522 2.142a3.9 3.9 0 0 1-1.431 1.476q-.9.531-2.061.531M63.35 11.67q.495 0 .711.252a.9.9 0 0 1 .216.621q0 .477-.306.747-.297.261-.765.261a1.4 1.4 0 0 1-.459-.072 9 9 0 0 0-.423-.153 1.4 1.4 0 0 0-.486-.081 1.7 1.7 0 0 0-.44.063q-.235.054-.469.162-.225.099-.405.252l-.198-.369q.513-.432.945-.747a9 9 0 0 1 .81-.522q.378-.207.693-.306a1.8 1.8 0 0 1 .576-.108m-2.574 1.629v5.4l1.35.459v.342h-4.31v-.342l1.286-.441v-5.211a1.2 1.2 0 0 0-.315-.252 3 3 0 0 0-.423-.225 6 6 0 0 0-.549-.216v-.261l2.61-.954h.216zm6.558 5.418 1.305.441v.342h-4.266v-.342l1.287-.441V8.691a2 2 0 0 0-.315-.297 3 3 0 0 0-.46-.297 6 6 0 0 0-.611-.306v-.243l2.96-.837h.208l-.108 1.593zm8.03-4.608q0-.738-.477-1.224t-1.404-.486q-.729 0-1.242.369t-.783 1.08q-.261.702-.261 1.71 0 .99.288 1.674.296.675.819 1.026a2.2 2.2 0 0 0 1.224.342q.612 0 1.188-.189a4 4 0 0 0 1.125-.612v.693q-.613.369-1.053.612a7 7 0 0 1-.774.369 3.3 3.3 0 0 1-.63.171 3.3 3.3 0 0 1-.603.054q-1.116 0-1.89-.459a3 3 0 0 1-1.161-1.296q-.387-.828-.387-1.899 0-1.062.35-1.881a4.1 4.1 0 0 1 .955-1.395 4.1 4.1 0 0 1 1.377-.873 4.5 4.5 0 0 1 1.629-.297q.441 0 .864.072t.873.225q.45.144.963.396l-.99.414V8.493a1.4 1.4 0 0 0-.288-.279 3 3 0 0 0-.396-.243 5 5 0 0 0-.504-.243v-.234l2.763-.783h.207l-.108 1.602v9.846q.09.099.225.189t.288.18q.162.081.324.144.162.054.315.09v.288l-2.448.621h-.216l-.162-1.395zM89.84 18.78q.667 0 1.27-.108a7 7 0 0 0 1.26-.36l-.477.369 1.367-2.511h.487l-.325 2.835q-.666.306-1.683.495-1.007.198-2.15.198-1.548 0-2.809-.405-1.25-.414-2.15-1.206a5.4 5.4 0 0 1-1.377-1.926q-.478-1.134-.478-2.583 0-1.908.856-3.33.854-1.431 2.456-2.214 1.603-.792 3.835-.792.566 0 1.124.072.56.063 1.324.243l-.55.117 1.144-.792h.224l.55 4.239-.46.099-1.593-2.799.486.504q-.746-.432-1.358-.594a5.1 5.1 0 0 0-1.314-.162q-1.17 0-2.07.324a3.9 3.9 0 0 0-1.495.927 3.9 3.9 0 0 0-.9 1.485q-.305.873-.305 1.989 0 1.431.377 2.529.387 1.098 1.072 1.845a4.6 4.6 0 0 0 1.629 1.134 5.4 5.4 0 0 0 2.033.378m7.71-.063 1.305.441v.342H94.59v-.342l1.287-.441V8.691a2 2 0 0 0-.315-.297 3 3 0 0 0-.459-.297 6 6 0 0 0-.612-.306v-.243l2.961-.837h.207l-.108 1.593zm6.725-4.104.117.684q-.963.216-1.575.45t-.954.477q-.333.243-.468.522-.126.27-.126.603 0 .594.324.9.333.306.819.306.378 0 .693-.198a1.45 1.45 0 0 0 .684-1.215v-3.294q0-.612-.324-.972-.315-.36-1.035-.36-.27 0-.603.072a2.6 2.6 0 0 0-.621.225l.405-.477a8 8 0 0 1-.09.612q-.054.27-.126.468a.74.74 0 0 1-.171.306.9.9 0 0 1-.378.234q-.225.081-.459.081-.315 0-.495-.135a.42.42 0 0 1-.18-.351q0-.315.297-.639.297-.333.783-.612a4.8 4.8 0 0 1 1.098-.459 4.4 4.4 0 0 1 1.242-.18q.864 0 1.377.261t.738.756.225 1.188v4.041q0 .261.072.432a.6.6 0 0 0 .225.261q.144.09.369.09.189 0 .414-.072.234-.081.459-.216v.468q-.405.405-.855.612a2.1 2.1 0 0 1-.837.198q-.486 0-.819-.198a1.3 1.3 0 0 1-.504-.585 2.3 2.3 0 0 1-.162-.9l.063-.072q-.135.54-.459.945a2.25 2.25 0 0 1-.774.612q-.45.216-.963.216-.918 0-1.512-.504-.594-.513-.594-1.485 0-.495.198-.909.207-.414.72-.774.513-.369 1.422-.711a23 23 0 0 1 2.34-.702m6.018-3.015q.567 0 1.008.108.441.099.981.351l.126 2.133h-.45l-.945-2.178.639.756a2.6 2.6 0 0 0-.648-.369 1.7 1.7 0 0 0-.657-.126q-.675 0-1.053.279a.91.91 0 0 0-.378.774q0 .405.216.666.225.261.621.468.396.198.927.414.405.153.756.36.36.207.63.495.27.279.423.666.162.378.162.9 0 .783-.387 1.323a2.4 2.4 0 0 1-1.062.81q-.666.27-1.521.27a5.3 5.3 0 0 1-1.017-.09 4.2 4.2 0 0 1-.891-.261l-.36-2.205h.459l1.242 2.268-1.143-.711q.333.153.576.234.243.072.45.099a5 5 0 0 0 .432.018q.828 0 1.314-.315.486-.324.486-.972 0-.342-.153-.576a1.34 1.34 0 0 0-.432-.405 4.4 4.4 0 0 0-.63-.333l-.729-.315a4.4 4.4 0 0 1-.891-.522 2.1 2.1 0 0 1-.594-.729q-.207-.441-.207-1.062 0-.711.36-1.206t.972-.756q.621-.261 1.368-.261m6.394 0q.567 0 1.008.108.441.099.981.351l.126 2.133h-.45l-.945-2.178.639.756a2.6 2.6 0 0 0-.648-.369 1.7 1.7 0 0 0-.657-.126q-.675 0-1.053.279a.91.91 0 0 0-.378.774q0 .405.216.666.225.261.621.468.396.198.927.414.405.153.756.36.36.207.63.495.27.279.423.666.162.378.162.9 0 .783-.387 1.323a2.4 2.4 0 0 1-1.062.81q-.666.27-1.521.27a5.3 5.3 0 0 1-1.017-.09 4.2 4.2 0 0 1-.891-.261l-.36-2.205h.459l1.242 2.268-1.143-.711q.333.153.576.234.243.072.45.099a5 5 0 0 0 .432.018q.828 0 1.314-.315.486-.324.486-.972 0-.342-.153-.576a1.34 1.34 0 0 0-.432-.405 4.4 4.4 0 0 0-.63-.333l-.729-.315a4.4 4.4 0 0 1-.891-.522 2.1 2.1 0 0 1-.594-.729q-.207-.441-.207-1.062 0-.711.36-1.206t.972-.756q.621-.261 1.368-.261m14.751-.783v4.716h-.729l-.198-1.899h-4.338v-.918h4.338l.198-1.899zm2.34.054-.45.099-1.629-2.862.567.261h-6.093V7.44h5.76l1.035-.486h.18zm-1.872 7.992 1.773-2.925.396.099-.684 4.032h-.198l-1.089-.567h-5.931v-.927h6.183zm-7.902.639v-.36l1.386-.486V8.286l-1.386-.486v-.36h3.24V19.5zm17.133-5.391q0-.738-.477-1.224t-1.404-.486q-.729 0-1.242.369t-.783 1.08q-.261.702-.261 1.71 0 .99.288 1.674.297.675.819 1.026a2.2 2.2 0 0 0 1.224.342q.612 0 1.188-.189a4 4 0 0 0 1.125-.612v.693q-.612.369-1.053.612a7 7 0 0 1-.774.369 3.3 3.3 0 0 1-.63.171 3.3 3.3 0 0 1-.603.054q-1.116 0-1.89-.459a3 3 0 0 1-1.161-1.296q-.387-.828-.387-1.899 0-1.062.351-1.881a4.1 4.1 0 0 1 .954-1.395 4.1 4.1 0 0 1 1.377-.873 4.5 4.5 0 0 1 1.629-.297q.441 0 .864.072t.873.225q.45.144.963.396l-.99.414V8.493a1.4 1.4 0 0 0-.288-.279 3 3 0 0 0-.396-.243 5 5 0 0 0-.504-.243v-.234l2.763-.783h.207l-.108 1.602v9.846q.09.099.225.189t.288.18q.162.081.324.144.162.054.315.09v.288l-2.448.621h-.216l-.162-1.395zm6.805 10.161q-.9 0-1.602-.144a3.9 3.9 0 0 1-1.17-.405 1.9 1.9 0 0 1-.711-.648 1.56 1.56 0 0 1-.243-.864q0-.351.171-.666.18-.315.576-.585.405-.27 1.044-.495t1.575-.414h.567v.207q-.954.153-1.485.414t-.738.603a1.5 1.5 0 0 0-.207.765q0 .468.261.81.27.351.819.531.549.189 1.395.189.828 0 1.44-.234.621-.234.963-.63.342-.387.342-.846a.96.96 0 0 0-.162-.558q-.162-.225-.522-.387-.351-.153-.954-.252-.594-.09-1.476-.153-.891-.063-1.458-.234-.558-.162-.873-.414a1.44 1.44 0 0 1-.432-.558 1.7 1.7 0 0 1-.108-.657q0-.657.576-1.098.585-.45 1.602-.639l.261.216q-.495.063-.747.288a.73.73 0 0 0-.243.576q0 .207.081.378.09.162.333.288.243.117.729.207.486.081 1.287.135a10.6 10.6 0 0 1 1.485.207q.657.144 1.107.405.459.27.693.675.243.405.243.981 0 .801-.531 1.485t-1.521 1.098q-.99.423-2.367.423m.045-6.966q-.954 0-1.701-.369a2.9 2.9 0 0 1-1.17-1.008 2.62 2.62 0 0 1-.423-1.467q0-.855.432-1.494a2.9 2.9 0 0 1 1.179-1.008 3.9 3.9 0 0 1 1.692-.36q.738 0 1.341.225t1.035.621q.441.396.675.909.243.513.243 1.089a2.63 2.63 0 0 1-.432 1.494 2.9 2.9 0 0 1-1.179 1.008q-.747.36-1.692.36m.081-.666q.684 0 1.071-.522.396-.531.396-1.539 0-1.116-.441-1.71-.441-.603-1.17-.603-.684 0-1.08.522t-.396 1.539q0 1.107.441 1.71.45.603 1.179.603m1.062-4.383-.252-.531q.639.09 1.134.09.495-.009.891-.063l.72-.117q.333-.063.63-.063.315 0 .477.18.171.171.171.54a.85.85 0 0 1-.243.63.75.75 0 0 1-.549.234.95.95 0 0 1-.513-.153l-.54-.351a2.7 2.7 0 0 0-.765-.324q-.459-.126-1.161-.072m7.554-.657q.927 0 1.593.405.666.396 1.053 1.17.396.765.486 1.854h-5.562l.018-.648 4.59-.225-.792.405a4.5 4.5 0 0 0-.234-1.17q-.18-.495-.504-.765-.315-.27-.81-.27-.558 0-.99.315t-.675.963q-.234.639-.234 1.62t.369 1.692q.378.711 1.053 1.098.684.378 1.584.378.36 0 .684-.081.333-.09.657-.261t.639-.423l.252.306a7 7 0 0 1-.963.954 3.6 3.6 0 0 1-1.035.585 3.5 3.5 0 0 1-1.188.198q-1.089 0-1.926-.513a3.64 3.64 0 0 1-1.305-1.404q-.468-.891-.468-2.016 0-1.143.441-2.088a3.7 3.7 0 0 1 1.269-1.512q.828-.567 1.998-.567"
      />
    </svg>
  );
}

function HamburgerIcon({
  isLight,
  className,
  barClassName,
}: {
  isLight: boolean;
  className?: string;
  barClassName?: string;
}) {
  const bar = cn(
    "w-full rounded-full h-[0.2rem] transition-colors duration-300",
    isLight ? "bg-black" : "bg-white",
    barClassName,
  );
  return (
    <span className={cn("flex flex-col w-[3.4rem] gap-[0.6rem]", className)}>
      <span className={bar} />
      <span className={bar} />
      <span className={bar} />
    </span>
  );
}

function CloseIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="h-[2.4rem] w-[2.4rem] shrink-0"
      aria-hidden>
      <path
        d="M5 5L19 19M19 5L5 19"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

type MenuLink = {
  title: string;
  href: string;
  isExternal?: boolean;
  description?: string;
  icon?: GROQImage;
};

type MenuEntry = MenuLink | { title: string; subItems: MenuLink[] };

function toMenuLink(title: string, link?: IPageLink | null): MenuLink {
  return {
    title,
    href: resolvePageLink(link ?? null) ?? "/",
    isExternal: link?.externalLink ?? false,
  };
}

function SubItemIcon({ icon }: { icon: GROQImage }) {
  return (
    <Image
      src={icon}
      alt=""
      className="shrink-0 rounded-[0.5rem] object-cover"
      style={{ width: "3.5rem", height: "3.5rem" }}
    />
  );
}

function DesktopCategory({
  title,
  subItems,
  isLight,
  checkIsActive,
  className,
}: {
  title: string;
  subItems: MenuLink[];
  isLight: boolean;
  checkIsActive: (href: string) => boolean;
  className?: string;
}) {
  const panelId = useId();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();
  const pointerType = useRef("");
  const [isOpen, setIsOpen] = useState(false);
  const isActive = subItems.some((sub) => checkIsActive(sub.href));

  const open = () => {
    clearTimeout(closeTimer.current);
    setIsOpen(true);
  };
  const close = () => {
    clearTimeout(closeTimer.current);
    setIsOpen(false);
  };

  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) close();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      close();
      triggerRef.current?.focus();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  return (
    <div
      ref={wrapperRef}
      className="relative flex"
      onPointerEnter={(e) => e.pointerType === "mouse" && open()}
      onPointerLeave={(e) => {
        if (e.pointerType !== "mouse") return;
        clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => setIsOpen(false), 150);
      }}
      onBlur={(e) => {
        // A null relatedTarget is a click on the panel's padding; outside clicks are handled on pointerdown.
        const next = e.relatedTarget as Node | null;
        if (next && !wrapperRef.current?.contains(next)) close();
      }}>
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onPointerDown={(e) => {
          pointerType.current = e.pointerType;
        }}
        onClick={() => {
          // Hover already opened it for a mouse; a click must not toggle it shut again.
          if (pointerType.current === "mouse") open();
          else setIsOpen((v) => !v);
          pointerType.current = "";
        }}
        className={cn(
          "inline-flex items-center gap-[0.5rem] font-geist text-[1.75rem] leading-[3rem] tracking-[-0.0875rem] transition-[opacity,color] duration-200 whitespace-nowrap",
          isLight ? "text-black" : "text-white",
          isActive
            ? "font-medium underline decoration-[#7bcdff] decoration-[0.15rem] underline-offset-[0.5rem]"
            : cn("font-normal hover:opacity-100", isOpen ? "opacity-100" : "opacity-60"),
          className,
        )}>
        {title}
        <ChevronDown
          aria-hidden
          className={cn(
            "w-[2rem] h-[2rem] shrink-0 transition-transform duration-200",
            isOpen && "rotate-180",
          )}
        />
      </button>

      <div
        id={panelId}
        className={cn(
          "absolute left-[calc(-2.25rem_-_1px)] top-full w-max pt-[0.375rem] transition-[opacity,visibility,transform] duration-200",
          isOpen ? "visible opacity-100" : "invisible opacity-0 -translate-y-[0.5rem]",
        )}>
        <ul className="flex flex-col gap-[0.625rem] whitespace-normal rounded-[0.5rem] border border-white/10 bg-black px-[0.75rem] py-[0.625rem] shadow-[0_0.5rem_2.5rem_rgba(0,0,0,0.24)]">
          {subItems.map((sub, i) => {
            const isSubActive = checkIsActive(sub.href);
            return (
              <li key={i}>
                <Link
                  href={sub.href}
                  target={sub.isExternal ? "_blank" : "_self"}
                  onClick={close}
                  className={cn(
                    "group flex min-w-[16.125rem] items-center gap-[1.5rem] rounded-[0.25rem] px-[1.5rem] py-[1rem] transition-colors duration-200 hover:bg-white/10",
                    isSubActive ? "text-white" : "text-white/50 hover:text-white",
                  )}>
                  {sub.icon && <SubItemIcon icon={sub.icon} />}
                  <span className="flex flex-col gap-[0.25rem] transition-transform duration-200 group-hover:translate-x-[0.75rem]">
                    <span
                      className={cn(
                        "font-geist text-[1.75rem] leading-[3rem] tracking-[-0.0875rem] whitespace-nowrap",
                        isSubActive
                          ? "font-medium underline decoration-[#7bcdff] decoration-[0.15rem] underline-offset-[0.5rem]"
                          : "font-normal",
                      )}>
                      {sub.title}
                    </span>
                    {sub.description && (
                      <span className="max-w-[32rem] font-geist text-[1.5rem] leading-[2rem] tracking-[-0.03rem] text-white/40">
                        {sub.description}
                      </span>
                    )}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function MobileCategory({
  title,
  subItems,
  checkIsActive,
  onNavigate,
  className,
}: {
  title: string;
  subItems: MenuLink[];
  checkIsActive: (href: string) => boolean;
  onNavigate: () => void;
  className?: string;
}) {
  const panelId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const isActive = subItems.some((sub) => checkIsActive(sub.href));

  return (
    <div className={className}>
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={() => setIsOpen((v) => !v)}
        className={cn(
          "flex w-full items-center justify-between gap-[1rem] py-[2rem] text-left font-geist text-[2.5rem] leading-[2.4rem] text-white transition-opacity duration-200",
          isActive || isOpen ? "opacity-80" : "opacity-60",
          isActive && "font-semibold",
        )}>
        <span
          className={cn(
            isActive &&
              "underline decoration-[#7bcdff] decoration-[0.1rem] underline-offset-[0.4rem]",
          )}>
          {title}
        </span>
        <ChevronDown
          aria-hidden
          className={cn(
            "w-[2.5rem] h-[2.5rem] shrink-0 transition-transform duration-300",
            isOpen && "rotate-180",
          )}
        />
      </button>

      <div
        id={panelId}
        className={cn(
          "grid transition-[grid-template-rows,visibility] duration-300 ease-out",
          isOpen ? "visible grid-rows-[1fr]" : "invisible grid-rows-[0fr]",
        )}>
        <div className="overflow-hidden">
          <div className="flex flex-col pb-[1.5rem]">
            {subItems.map((sub, i) => {
              const isSubActive = checkIsActive(sub.href);
              return (
                <Link
                  key={i}
                  href={sub.href}
                  target={sub.isExternal ? "_blank" : "_self"}
                  onClick={onNavigate}
                  className={cn(
                    "flex items-center gap-[1.5rem] py-[1.25rem] font-geist text-[2rem] leading-[2.4rem] text-white transition-opacity duration-200",
                    isSubActive
                      ? "font-semibold opacity-80"
                      : "font-normal opacity-60",
                  )}>
                  {sub.icon && <SubItemIcon icon={sub.icon} />}
                  <span
                    className={cn(
                      isSubActive &&
                        "underline decoration-[#7bcdff] decoration-[0.1rem] underline-offset-[0.4rem]",
                    )}>
                    {sub.title}
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function normalizePath(path: string): string {
  const trimmed = path.replace(/\/+$/, "") || "/";
  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

export default function Header({
  data,
  headerStyle,
  headerType = "main",
  variant = "default",
  className,
  navClassName,
  contentClassName,
  logoClassName,
  solidLogoIcon,
  menuClassName,
  menuItemClassName,
  menuStyle,
  actionsClassName,
  actionsStyle,
  loginClassName,
  loginStyle,
  signupClassName,
  signupStyle,
  hamburgerButtonClassName,
  hamburgerClassName,
  hamburgerBarClassName,
  menuItems,
  initialActiveHref,
}: {
  data: IHeader;
  headerStyle?: "dark" | "light";
  headerType?: "main" | "lp" | "hidden";
  variant?: "default" | "slim";
  ctaLoginButtonType?: "default" | "green" | "gray" | "transparent" | "login";
  ctaButtonType?: "default" | "green" | "gray" | "transparent" | "login";
  showBorder?: boolean;
  className?: string;
  navClassName?: string;
  contentClassName?: string;
  logoClassName?: string;
  solidLogoIcon?: boolean;
  menuClassName?: string;
  menuItemClassName?: string;
  menuStyle?: CSSProperties;
  actionsClassName?: string;
  actionsStyle?: CSSProperties;
  loginClassName?: string;
  loginStyle?: CSSProperties;
  signupClassName?: string;
  signupStyle?: CSSProperties;
  hamburgerButtonClassName?: string;
  hamburgerClassName?: string;
  hamburgerBarClassName?: string;
  menuItems?: MenuEntry[];
  initialActiveHref?: string;
}) {
  const pathnames = useGlobalPathname();
  const navRef = useRef<HTMLElement>(null);
  const mobileMenuTriggerRef = useRef<HTMLButtonElement>(null);
  const mobileMenuId = useId();

  const [isOpen, setIsOpen] = useState(false);
  const [theme, setTheme] = useState<"dark" | "light">(headerStyle ?? "dark");
  const [activeHash, setActiveHash] = useState("");

  const items = useMemo<MenuEntry[]>(
    () => {
      if (menuItems) return menuItems;

      return (data?.menu ?? []).flatMap((item): MenuEntry[] => {
        if (!item.isCategory) return [toMenuLink(item.title, item.link)];

        const subItems = [
          ...(item.subItems ?? []).map((sub) => ({
            ...toMenuLink(sub.title, sub.link),
            description: sub.description,
            icon: sub.icon?.src ? sub.icon : undefined,
          })),
          ...(item.socialSubItems ?? []).map((sub) => ({
            ...toMenuLink(sub.title, sub.link),
            icon: sub.image?.src ? sub.image : undefined,
          })),
        ];
        return subItems.length > 0 ? [{ title: item.title, subItems }] : [];
      });
    },
    [data?.menu, menuItems],
  );

  const isLight = theme === "light";

  useEffect(() => {
    if (headerStyle) {
      setTheme(headerStyle);
      return;
    }
    const marked = Array.from(
      document.querySelectorAll<HTMLElement>("[data-navbar-theme]"),
    );
    if (marked.length === 0) return;

    const update = () => {
      const nav = navRef.current;
      if (!nav) return;
      const rect = nav.getBoundingClientRect();
      const line = rect.top + rect.height / 2;

      let next: "dark" | "light" = "dark";
      for (const el of marked) {
        const box = el.getBoundingClientRect();
        if (line >= box.top && line <= box.bottom) {
          next = el.dataset.navbarTheme === "light" ? "light" : "dark";
          break;
        }
      }
      setTheme(next);
    };

    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [headerStyle]);

  useEffect(() => {
    const anchorIds = items
      .flatMap((it) => ("subItems" in it ? it.subItems : [it]))
      .map((it) => (it.href.includes("#") ? it.href.split("#")[1] : null))
      .filter(Boolean) as string[];
    if (anchorIds.length === 0) return;

    const sections = anchorIds
      .map((id) => document.getElementById(id))
      .filter(Boolean) as HTMLElement[];
    if (sections.length === 0) return;

    const update = () => {
      const line = window.innerHeight * 0.3;
      let current = "";
      for (const el of sections) {
        if (el.getBoundingClientRect().top <= line) current = el.id;
        else break;
      }
      setActiveHash(current ? `#${current}` : "");
    };

    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [items]);

  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const desktop = window.matchMedia("(min-width: 993px)");
    const closeOnDesktop = () => {
      if (desktop.matches) setIsOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setIsOpen(false);
      mobileMenuTriggerRef.current?.focus({ preventScroll: true });
    };
    closeOnDesktop();
    desktop.addEventListener("change", closeOnDesktop);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      desktop.removeEventListener("change", closeOnDesktop);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  if (!data || headerType === "hidden") return null;

  const checkIsActive = (href: string): boolean => {
    if (!activeHash && initialActiveHref === href) return true;

    const [linkPath, linkHash] = href.split("#");
    const normalizedLinkPath = normalizePath(linkPath || "/");
    const normalizedCurrentPath = normalizePath(pathnames.currentPathname);

    if (!href.includes("#")) {
      return normalizedCurrentPath === normalizedLinkPath;
    }
    return (
      normalizedCurrentPath === normalizedLinkPath &&
      activeHash === `#${linkHash}`
    );
  };

  const loginCTA = data.headerLoginCTA;
  const signupCTA = data.headerCTA;
  const loginLabel = loginCTA?.cta?.title ?? null;
  const signupLabel = signupCTA?.cta?.title ?? null;
  const showMenu = headerType === "main";

  return (
    <>
      <div className={cn("relative z-50 left-0 right-0 top-0 w-full", className)}>
        <nav
          ref={navRef}
          className={cn(
            "w-full border-b shadow-[0_0.5rem_2.5rem_rgba(0,0,0,0.24)] transition-colors duration-300",
            isLight
              ? variant === "slim"
                ? "border-black/10 bg-white/60"
                : "border-black/10 bg-white/30"
              : variant === "slim"
                ? "border-white/10 bg-black/[0.55]"
                : "border-white/10 bg-black/20",
            navClassName,
          )}>
          <div
            className={cn(
              "flex justify-between items-center mx-auto w-[min(150rem,100%)] px-[2rem] lg:px-[0rem] gap-[2rem]",
              variant === "slim" ? "py-[1rem]" : "py-[2rem]",
              contentClassName,
            )}>

            {/* Logo */}
            <Link
              href="/"
              className="flex items-center shrink-0">
              <Logo
                isLight={isLight}
                solidIcon={solidLogoIcon}
                className={logoClassName}
              />
            </Link>

            {showMenu && items.length > 0 && (
              <div
                className={cn("hidden items-center whitespace-nowrap lg:flex gap-[2.75rem]", menuClassName)}
                style={menuStyle}>
                {items.map((it, i) => {
                  if ("subItems" in it) {
                    return (
                      <DesktopCategory
                        key={i}
                        title={it.title}
                        subItems={it.subItems}
                        isLight={isLight}
                        checkIsActive={checkIsActive}
                        className={menuItemClassName}
                      />
                    );
                  }
                  const isActive = checkIsActive(it.href);
                  return (
                    <Link
                      key={i}
                      href={it.href}
                      target={it.isExternal ? "_blank" : "_self"}
                      className={cn(
                        "font-geist text-[1.75rem] leading-[3rem] tracking-[-0.0875rem] transition-[opacity,color] duration-200 whitespace-nowrap",
                        isLight ? "text-black" : "text-white",
                        isActive
                          ? "font-medium underline decoration-[#7bcdff] decoration-[0.15rem] underline-offset-[0.5rem]"
                          : "font-normal opacity-60 hover:opacity-100",
                        menuItemClassName,
                      )}>
                      {it.title}
                    </Link>
                  );
                })}
              </div>
            )}

            {showMenu && (
              <div
                className={cn("hidden items-center lg:flex shrink-0 gap-[0.8rem]", actionsClassName)}
                style={actionsStyle}>
                {loginLabel && (
                  <CTAWrapper
                    cta={loginCTA?.cta}
                    className={cn(
                      "flex items-center font-medium capitalize transition-colors duration-200 rounded-[1.25rem] px-[2.75rem] pt-[1.125rem] pb-[1.25rem] font-geist text-[1.75rem] leading-[1.5] tracking-[-0.0875rem]",
                      isLight ? "text-black hover:bg-black/5" : "text-white hover:bg-white/10",
                      loginClassName,
                    )}
                    style={loginStyle}>
                    {loginLabel}
                  </CTAWrapper>
                )}
                {signupLabel && (
                  <CTAWrapper
                    cta={signupCTA?.cta}
                    className={cn(
                      "flex items-center font-medium capitalize transition-colors duration-200 rounded-[1.25rem] px-[2.75rem] pt-[1.125rem] pb-[1.25rem] font-geist text-[1.75rem] leading-[1.5] tracking-[-0.0875rem] cursor-pointer",
                      isLight
                        ? "bg-black text-white hover:bg-black/90"
                        : "bg-white text-black hover:bg-white/90",
                      signupClassName,
                    )}
                    style={{
                      ...signupStyle,
                      textShadow: "0 0.125rem 0.125rem rgba(255,255,255,0.23)",
                    }}>
                    {signupLabel}
                  </CTAWrapper>
                )}
              </div>
            )}

            {showMenu && (
              <button
                type="button"
                ref={mobileMenuTriggerRef}
                aria-label="Open menu"
                aria-expanded={isOpen}
                aria-controls={mobileMenuId}
                onClick={() => setIsOpen(true)}
                className={cn(
                  "flex justify-center items-center lg:hidden shrink-0 pr-[0.75rem]",
                  hamburgerButtonClassName,
                )}>
                <HamburgerIcon
                  isLight={isLight}
                  className={hamburgerClassName}
                  barClassName={hamburgerBarClassName}
                />
              </button>
            )}
          </div>
        </nav>
      </div>

      {showMenu && (
        <div
          id={mobileMenuId}
          aria-hidden={!isOpen}
          {...{ inert: !isOpen }}
          className={cn(
            "fixed inset-0 z-[60] flex flex-col overflow-y-auto overscroll-contain bg-black p-[2rem] pb-[3rem] transition-transform duration-300 ease-out lg:hidden px-[3.75rem]",
            isOpen ? "translate-y-0" : "-translate-y-full pointer-events-none",
          )}>
          <div className="flex shrink-0 justify-between items-center h-[5rem]">
            <Link
              href="/"
              onClick={() => setIsOpen(false)}
              className="shrink-0">
              <Logo solidIcon />
            </Link>
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => {
                setIsOpen(false);
                mobileMenuTriggerRef.current?.focus({ preventScroll: true });
              }}
              className="flex justify-center items-center p-[1rem] -mr-[1rem]">
              <CloseIcon />
            </button>
          </div>

          <div className="flex shrink-0 flex-col mt-[3rem]">
            {items.map((it, i) => {
              if ("subItems" in it) {
                return (
                  <MobileCategory
                    key={i}
                    title={it.title}
                    subItems={it.subItems}
                    checkIsActive={checkIsActive}
                    onNavigate={() => setIsOpen(false)}
                    className={cn(i < items.length - 1 && "border-b border-white/10")}
                  />
                );
              }
              const isActive = checkIsActive(it.href);
              return (
                <Link
                  key={i}
                  href={it.href}
                  target={it.isExternal ? "_blank" : "_self"}
                  onClick={() => setIsOpen(false)}
                  className={cn(
                    "py-[2rem] font-geist text-[2.5rem] leading-[2.4rem] text-white transition-opacity duration-200",
                    i < items.length - 1 && "border-b border-white/10",
                    isActive
                      ? "font-semibold underline decoration-[#7bcdff] decoration-[0.1rem] underline-offset-[0.4rem] opacity-80"
                      : "font-normal opacity-60",
                  )}>
                  {it.title}
                </Link>
              );
            })}
          </div>

          {signupLabel && (
            <div
              onClick={() => setIsOpen(false)}
              className="mt-auto w-full shrink-0 cursor-pointer">
              <CTAWrapper
                cta={signupCTA?.cta}
                className="flex justify-center items-center w-full font-medium text-black capitalize transition-colors duration-200 cursor-pointer rounded-[1rem] bg-[#98d8ff] px-[4rem] py-[1.4rem] font-geist text-[1.6rem] tracking-[-0.08rem] hover:bg-[#85cdfa]">
                {signupLabel}
              </CTAWrapper>
            </div>
          )}
        </div>
      )}
    </>
  );
}
